import endpointMaker from "configuration/endpoints";
import request from "http/request";
import { ILocationItem } from "model/geo/location";
import React from "react";

const CHUNK_SIZE = 500;

const resolvedLocations = new Map<string, ILocationItem | null>();
const failedLocations = new Map<string, string>();

const toKey = (id: string | number): string => String(id);

const toChunks = (ids: string[]): string[][] => {
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
    chunks.push(ids.slice(i, i + CHUNK_SIZE));
  }
  return chunks;
};

const indexById = (items: ILocationItem[]): Map<string, ILocationItem> => {
  const byId = new Map<string, ILocationItem>();
  items.forEach((item) => {
    if (!item) return;
    byId.set(toKey(item.id), item);
    item.ids?.forEach((id) => byId.set(toKey(id), item));
  });
  return byId;
};

export const getLocationLabel = ({
  targetType,
  canonicalName,
  region,
  country,
}: ILocationItem): string => {
  if (targetType === "COUNTRY") return canonicalName;
  if (targetType === "REGION") return `${canonicalName}, ${country}`;
  return `${canonicalName}, ${region}, ${country}`;
};

export const locationToCityRegionCountryString = (location: any) => {
  let label = "";

  if (location.city) {
    label += `${location.city}, `;
  }

  if (location.region) {
    label += `${location.region}, `;
  }

  label += location.country;
  return label;
};

export const cacheLocations = (items: ILocationItem[] = []): void => {
  items.forEach((item) => {
    if (!item) return;
    resolvedLocations.set(toKey(item.id), item);
    failedLocations.delete(toKey(item.id));
  });
};

export const getCachedLocation = (
  id: string | number
): ILocationItem | null | undefined => resolvedLocations.get(toKey(id));

export const getLocationError = (
  ids: Array<string | number> = []
): string | undefined => {
  const failed = ids.map(toKey).find((id) => failedLocations.has(id));
  return failed ? failedLocations.get(failed) : undefined;
};

const storeResolved = (ids: string[], items: ILocationItem[]): void => {
  const byId = indexById(items);
  ids.forEach((id) => {
    resolvedLocations.set(id, byId.get(id) ?? null);
    failedLocations.delete(id);
  });
};

const storeFailed = (ids: string[], message: string): void => {
  ids.forEach((id) => failedLocations.set(id, message));
};

export const resolveLocations = async (
  ids: Array<string | number> = []
): Promise<void> => {
  const missing = Array.from(new Set(ids.map(toKey))).filter(
    (id) => !resolvedLocations.has(id)
  );
  if (!missing.length) return;

  missing.forEach((id) => failedLocations.delete(id));

  await Promise.all(
    toChunks(missing).map((chunk) =>
      request
        .postJson(endpointMaker.geodb.searchByIds(), chunk.map(Number))
        .then((items: ILocationItem[]) => storeResolved(chunk, items || []))
        .catch((e) => storeFailed(chunk, e?.message || "Unknown error"))
    )
  );
};

export interface ILocationItems {
  items: Record<string, ILocationItem | null>;
  loading: boolean;
  error?: string;
}

export function useLocationItems(
  ids: Array<string | number> = []
): ILocationItems {
  const key = ids.map(toKey).join(",");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const requested = React.useMemo(() => ids.map(toKey), [key]);
  const [, setResolvedCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    const missing = requested.filter((id) => !resolvedLocations.has(id));
    if (!missing.length) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    resolveLocations(missing).then(() => {
      if (cancelled) return;
      setLoading(false);
      setResolvedCount((count) => count + 1);
    });

    return () => {
      cancelled = true;
    };
  }, [requested]);

  const items: Record<string, ILocationItem | null> = {};
  requested.forEach((id) => {
    items[id] = resolvedLocations.get(id) ?? null;
  });

  return { items, loading, error: getLocationError(requested) };
}
